import { useEffect, useState, useCallback } from 'react';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import { useFormik } from 'formik';
import jwt_decode from 'jwt-decode';

import { AddEditInventoryItemModal } from './ActionModals/AddEditInventoryItemModal';
import { DeleteInventoryItemConfirmationModal } from './ActionModals/DeleteInventoryItemConfirmationModal';
import { InventoryItemsTable } from './Components/InventoryItemsTable';
import { PropertySelector } from './Components/PropertySelector';
import { GenerateInventoryReport } from './Components/GenerateReport';
import {
  getAllInventoryItems,
  createInventoryItem,
  updateInventoryItem,
  deleteInventoryItem,
  getPropertyList,
} from './Helpers/Api';
import {
  getValidationSchema,
  getInitialValues,
} from './Helpers/Config';
import { generateReportOptions } from './Helpers/Utils';

const InventoryItemManagement = () => {
  const dispatch = useDispatch();
  const [properties, setProperties] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [modal, setModal] = useState('');
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [user, setUser] = useState(null); 
  const toggleReportModal = () => setIsReportModalOpen(!isReportModalOpen);

  const [selectedItem, setSelectedItem] = useState(null);

  const [selectedProperty, setSelectedProperty] = useState({
    property_id: '',
    building_name: ''
  });

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch {
      return null;
    }
  }, []);

  const fetchProperties = useCallback(async () => {
    const currentUser = getUserFromToken();
    if (!currentUser) return;

    // Set user state
    setUser(currentUser);

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const result = await getPropertyList(currentUser?.role || '', currentUser?.id || '');
      setProperties(result?.propertyList || []);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Failed to fetch properties.',
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch, getUserFromToken]);

  const fetchInventoryItems = useCallback(async (propertyId, buildingName) => {
    if (!propertyId || !buildingName) {
      setInventoryItems([]);
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const { items } = await getAllInventoryItems(propertyId, buildingName);
      setInventoryItems(items);
    } catch (error) {
      setInventoryItems([]);
      console.error('Error fetching inventory items:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch]);

  const handlePropertySelectionChange = useCallback((newSelection) => {
    setSelectedProperty(newSelection);
  }, []);

  const formik = useFormik({
    initialValues: getInitialValues(modal, selectedItem, selectedProperty),
    validationSchema: getValidationSchema(modal),
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const currentUser = getUserFromToken();

        // Use selected property state for API calls
        const inventoryData = {
          ...values,
          property_id: selectedProperty.property_id,
          building_name: selectedProperty.building_name
        };

        if (modal === 'add') {
          const inventoryItemData = {
            ...inventoryData,
            items: values.items.map(item => ({
              ...item,
              images: item.images?.filter(img => img.url).map(img => ({
                ...img,
                uploadedBy: {
                  id: currentUser?.id || '',
                  role: currentUser?.role || '',
                }
              })) || [],
              invoices: item.invoices?.filter(inv => inv.url).map(inv => ({
                ...inv,
                uploadedBy: {
                  id: currentUser?.id || '',
                  role: currentUser?.role || '',
                }
              })) || []
            }))
          };

          await createInventoryItem(inventoryItemData);
          await Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Inventory items saved successfully!'
          });
        } else if (modal === 'edit') {
          const editData = {
            item_id: values.item_id,
            property_id: selectedProperty.property_id,
            building_name: selectedProperty.building_name,
            item: {
              ...values.item,
              images: values.item.images?.filter(img => img.url).map(img => ({
                ...img,
                uploadedBy: {
                  id: currentUser?.id || '',
                  role: currentUser?.role || '',
                }
              })) || [],
              invoices: values.item.invoices?.filter(inv => inv.url).map(inv => ({
                ...inv,
                uploadedBy: {
                  id: currentUser?.id || '',
                  role: currentUser?.role || '',
                }
              })) || []
            }
          };

          await updateInventoryItem(editData);
          await Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Inventory item updated successfully!'
          });
        }

        // Refresh inventory items after successful operation
        await fetchInventoryItems(selectedProperty.property_id, selectedProperty.building_name);
        closeModal();
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: error.message });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
        setSubmitting(false);
      }
    },
  });

  const openAddModal = () => {
    setSelectedItem(null);
    setModal('add');
  };

  const openEditModal = (item) => {
    setSelectedItem(item);
    setModal('edit');
  };

  const openDeleteModal = (item) => {
    setSelectedItem(item);
    setModal('delete');
  };

  const closeModal = () => {
    formik.resetForm({
      values: getInitialValues('', null, selectedProperty),
    });
    setModal('');
    setSelectedItem(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await deleteInventoryItem(selectedItem._id);
      await Swal.fire({
        icon: 'success',
        title: 'Deleted',
        text: 'Inventory item deleted successfully!'
      });
      await fetchInventoryItems(selectedProperty.property_id, selectedProperty.building_name);
      closeModal();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    fetchInventoryItems(selectedProperty.property_id, selectedProperty.building_name);
  }, [selectedProperty.property_id, selectedProperty.building_name, fetchInventoryItems]);

  return (
    <div className="page-content">
      <MetaTags>
        <title>Rentdigicare | Inventory Item Management</title>
      </MetaTags>
      <div className="container-fluid">
        <Breadcrumbs title="Inventory" breadcrumbItem="Inventory Items" />

        <PropertySelector
          propertyList={properties}
          selectedProperty={selectedProperty}
          onPropertySelectionChange={handlePropertySelectionChange}
          onAddClick={openAddModal}
          setInventoryItems={setInventoryItems}
          toggleReportModal={toggleReportModal}
        />

        <GenerateInventoryReport
          propertyId={selectedProperty.property_id}
          buildingName={selectedProperty.building_name}
          toggle={toggleReportModal}
          modal={isReportModalOpen}
          options={generateReportOptions(inventoryItems)}
          user={user}
        />

        <InventoryItemsTable
          items={inventoryItems}
          onEdit={openEditModal}
          onDelete={openDeleteModal}
        />

        <AddEditInventoryItemModal
          open={modal === 'add' || modal === 'edit'}
          mode={modal}
          formik={formik}
          onClose={closeModal}
          propertyList={properties}
          selectedProperty={selectedProperty}
          itemId={selectedItem?._id || ''}
          user={user}
        />

        <DeleteInventoryItemConfirmationModal
          open={modal === 'delete'}
          onClose={closeModal}
          onDelete={handleDeleteConfirm}
          item={selectedItem}
        />
      </div>
    </div>
  );
};

export { InventoryItemManagement };